import React, { useState, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoginForm from '@/components/LoginForm';
import LoginVerification, {
  Credentials,
  MfaType
} from '@/components/LoginVerification';
import useDocumentTitle from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import { useAlert } from '@/components/Alert';
import Cookie from '@/utils/cookie';
import { parseXeroSignupFromCookie } from '@/utils/xeroSignup';
import { hasResetPasswordFlow } from '@/utils/passwordResetEntry';
import {
  REDIRECT_COOKIE,
  REDIRECT_PARAM,
  resolveRedirectUrl
} from '@/utils/redirect';

import './Login.scss';
import AuthPageLayout from '@/components/AuthPageLayout';
import { APP_FLAGS } from '@/config/flags';

export { REDIRECT_PARAM, REDIRECT_COOKIE };

type LoginProps = {
  resetPasswordEmail?: string;
};

const Login = ({ resetPasswordEmail }: LoginProps) => {
  useDocumentTitle('Login | CreditorWatch');
  const flags = useFlags();
  const [showMFA, setShowMFA] = useState<boolean>(false);
  const [verifyMessage, setVerifyMessage] = useState<string>('');
  const [mfaType, setMfaType] = useState<MfaType>(null);
  const [credentials, setCredentials] = useState<Credentials>(null);
  const [searchParams] = useSearchParams();
  const isUniversalLogin =
    flags?.[APP_FLAGS.ENABLE_AUTH0_UNIVERSAL_LOGIN] === true;
  const alert = useAlert();
  const xeroSignUpMsgRef = useRef(null);
  const xeroSignupCookie = parseXeroSignupFromCookie();

  const message = searchParams.get('message');
  const isPasswordResetSuccess = message === 'password-reset-success';
  const isPasswordResetLinkInvalid = message === 'invalid-reset-link';
  const isNewUser = searchParams.get('source') === 'welcome';
  const isPasswordResetFlow = hasResetPasswordFlow(searchParams);
  const isCenteredLayout = searchParams.get('layout') === 'middle';

  useEffect(() => {
    if (!isPasswordResetFlow) handleUserSignUpXero();
  }, []);

  const handleUserSignUpXero = () => {
    if (isFromXeroSignUp()) {
      showExistingAccountMsg();
    }
  };

  const setEmailField = () => {
    return isFromXeroSignUp() ? xeroSignupCookie.email : '';
  };

  const isFromXeroSignUp = () => {
    const redirectUrl = searchParams.get('redirectUrl');
    const onlyFromXeroRegex = new RegExp('./xero$');
    return xeroSignupCookie.email.length && onlyFromXeroRegex.test(redirectUrl);
  };

  const showExistingAccountMsg = () => {
    if (!alert.isActive(xeroSignUpMsgRef.current)) {
      xeroSignUpMsgRef.current = alert.open({
        variant: 'info',
        content:
          'It looks like you already have a CreditorWatch account. You can log in to your account to continue with your Xero setup or select the forgot password option if you need to.',
        duration: 5000
      });
    }
  };

  const handleVerification = (mfaType, msg, credentials) => {
    setShowMFA(true);
    setVerifyMessage(msg);
    setMfaType(mfaType);
    setCredentials(credentials);
  };

  const redirect = async (defaultRedirectUrl: string) => {
    const redirectCookie = Cookie.get(REDIRECT_COOKIE);
    const redirectParam = searchParams.get(REDIRECT_PARAM);

    Cookie.remove(REDIRECT_COOKIE);
    const redirectUrl = resolveRedirectUrl({
      cookie: redirectCookie,
      param: redirectParam,
      fallback: defaultRedirectUrl
    });
    window.location.assign(redirectUrl);
  };

  return (
    <>
      <AuthPageLayout title='Login | CreditorWatch' centered={isCenteredLayout}>
        <LoginForm
          onVerification={handleVerification}
          onLoginSuccess={redirect}
          xeroEmail={setEmailField()}
          initialEmail={isPasswordResetFlow ? resetPasswordEmail ?? '' : undefined}
          isPasswordResetFlow={isPasswordResetFlow}
          isPasswordResetSuccess={isPasswordResetSuccess}
          isPasswordResetLinkInvalid={isPasswordResetLinkInvalid}
          isNewUser={isNewUser}
          isUniversalLogin={isUniversalLogin}
          centeredLayout={isCenteredLayout}
        />
      </AuthPageLayout>
      {showMFA && (
        <LoginVerification
          mfaType={mfaType}
          title={verifyMessage}
          credentials={credentials}
          onSuccess={redirect}
        />
      )}
    </>
  );
};

export default Login;
